import { KeyboardEvent, useCallback, useRef, useState } from "react";
import styled from "styled-components";
import styles from "./AddressInput.module.css";
import { FLEX } from "styles/flex";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng,
} from "use-places-autocomplete";
import useOnclickOutside from "react-cool-onclickoutside";
import Input from "../Input";
import { checkEmpty, toTrim } from "utils/functions";

const Wrapper = styled.div`
  ${FLEX.FlexBetweenCenter};
  width: 100%;
  display: inline-block;
  position: relative;
`;

let cachedVal = "";
const acceptedKeys = ["ArrowUp", "ArrowDown", "Escape", "Enter"];
type Suggestion = google.maps.places.AutocompletePrediction;

export type AddressComponents = {
  address: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
};

type AddressComponent = google.maps.GeocoderAddressComponent;

const FALLBACK_CITY_TYPES = [
  "postal_town",
  "sublocality_level_1",
  "political",
  "sublocality",
  "neighborhood",
];

function findComponentByType(
  components: AddressComponent[],
  type: string
): AddressComponent | undefined {
  return components.find((component) => component.types.includes(type));
}

function findFallbackCity(
  components: AddressComponent[],
  formattedAddress: string
): AddressComponent | undefined {
  const formattedParts = formattedAddress
    .split(",")
    .map((part) => toTrim(part).toLowerCase())
    .filter(Boolean);

  return components.find((component) => {
    if (!FALLBACK_CITY_TYPES.some((type) => component.types.includes(type))) {
      return false;
    }

    const longName = component.long_name?.toLowerCase() ?? "";
    const shortName = component.short_name?.toLowerCase() ?? "";

    return formattedParts.some(
      (part) => longName.includes(part) || shortName.includes(part)
    );
  });
}

function parseAddress(
  components: AddressComponent[],
  formattedAddress: string
): AddressComponents {
  const streetNumber = findComponentByType(
    components,
    "street_number"
  )?.long_name;
  const route = findComponentByType(components, "route")?.long_name;
  const sublocalities = components
    .filter((component) =>
      component.types.some((type) => type.includes("sublocality"))
    )
    .map((component) => component.long_name);

  const preferredCity =
    findComponentByType(components, "locality") ??
    findFallbackCity(components, formattedAddress);

  const state = findComponentByType(components, "administrative_area_level_1");
  const zipcode = findComponentByType(components, "postal_code");
  const country = findComponentByType(components, "country");

  const addressLine = [streetNumber, route || sublocalities.join(", ")]
    .filter(Boolean)
    .join(" ");

  return {
    address: addressLine,
    city: preferredCity?.long_name ?? "",
    state: state?.short_name ?? "",
    zipcode: zipcode?.long_name ?? "",
    country: country?.long_name ?? "",
  };
}

const PlacesAutocomplete = (props: {
  onChangeLocationSelect: (
    address: google.maps.LatLngLiteral,
    addressString: string,
    addressComponents: AddressComponents
  ) => void;
  onChange?: (e: any) => void;
  defaultValue?: string;
  isDisabled?: boolean;
  value?: string;
  searchQuery?: string;
  isValidated?: boolean;
  tabIndex?: number;
  "data-testid"?: string;
}): JSX.Element => {
  const [currIndex, setCurrIndex] = useState<number | null>(null);
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      /* Define search scope here */
    },
    debounce: 300,
  });

  const hasSuggestions = status === "OK";
  const prevSearchQueryRef = useRef<string | undefined>(undefined);

  const dismissSuggestions = () => {
    setCurrIndex(null);
    clearSuggestions();
  };

  const ref = useOnclickOutside(dismissSuggestions);

  const handleInput = (e: any) => {
    // Update the keyword of the input element
    setValue(e.target.value);
    cachedVal = e.target.value;
    if (props.onChange) {
      props.onChange(e);
    }
  };

  const handleEnter = (idx: number) => () => {
    setCurrIndex(idx);
  };

  const handleLeave = () => {
    setCurrIndex(null);
  };
  const handleSelect =
    ({ description, place_id }: any) =>
    () => {
      // When user selects a place, we can replace the keyword without request data from API
      // by setting the second parameter to "false"
      setValue(description, false);
      clearSuggestions();

      // Get latitude and longitude via utility functions
      getGeocode({ placeId: place_id })
        .then((results) => {
          if (results.length > 0) {
            const { lat, lng } = getLatLng(results[0]);
            props.onChangeLocationSelect(
              { lat, lng },
              results[0].formatted_address,
              parseAddress(
                results[0].address_components,
                results[0].formatted_address
              )
            );
          }
        })
        .catch((error) => {
          console.log("Geocoding failed:", error);
          // ZERO_RESULTS나 다른 오류가 발생해도 처리 계속
        });
    };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!hasSuggestions || !acceptedKeys.includes(e.key)) return;

    if (e.key === "Enter" || e.key === "Escape") {
      dismissSuggestions();
      return;
    }

    let nextIndex: number | null;

    if (e.key === "ArrowUp") {
      e.preventDefault();
      nextIndex = currIndex ?? data.length;
      nextIndex = nextIndex && nextIndex > 0 ? nextIndex - 1 : null;
    } else {
      nextIndex = currIndex ?? -1;
      nextIndex = nextIndex < data.length - 1 ? nextIndex + 1 : null;
    }

    setCurrIndex(nextIndex);
    // @ts-ignore
    setValue(data[nextIndex] ? data[nextIndex].description : cachedVal, false);
  };

  const renderSuggestions = (): JSX.Element => {
    const suggestions = data.map((suggestion: Suggestion, idx: number) => {
      const {
        place_id,
        structured_formatting: { main_text, secondary_text },
      } = suggestion;

      return (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events
        <li
          key={place_id}
          id={`ex-list-item-${idx}`}
          className={styles.list_item}
          onClick={handleSelect(suggestion)}
          onMouseEnter={handleEnter(idx)}
          role="option"
          aria-selected={idx === currIndex}
          value={props.value}
          data-testid="atoms-input-address-li-click"
        >
          <strong>{main_text}</strong>
          <small className={styles.sub_text}>{secondary_text}</small>
        </li>
      );
    });

    return <>{suggestions}</>;
  };

  const handleKeyPress = useCallback(() => {
    if (value !== undefined && !checkEmpty(props.value)) {
      setValue(value, false);
      clearSuggestions();

      // Get latitude and longitude via utility functions
      getGeocode({ address: value })
        .then((results) => {
          if (results.length > 0) {
            const { lat, lng } = getLatLng(results[0]);
            if (lat && lng) {
              props.onChangeLocationSelect(
                { lat, lng },
                results[0].formatted_address,
                parseAddress(
                  results[0].address_components,
                  results[0].formatted_address
                )
              );
            }
          }
        })
        .catch((error) => {
          console.log("Geocoding failed:", error);
          // ZERO_RESULTS나 다른 오류가 발생해도 처리 계속
        });
    }
  }, [clearSuggestions, setValue, value, props]);

  const handleBlur = useCallback(() => {
    // suggestions가 열려있고 선택된 항목이 있으면 handleKeyPress를 호출하지 않음
    if (hasSuggestions && currIndex !== null) {
      return;
    }

    // onClick 이벤트가 먼저 처리되도록 150ms 지연
    setTimeout(() => {
      handleKeyPress();
    }, 150);
  }, [currIndex, handleKeyPress, hasSuggestions]);

  // const searchRef = useRef<any>(null);
  // useEffect(() => {
  //   // 특정 영역 외 클릭 시 발생하는 이벤트
  //   function handleFocus(e: any) {
  //     if (searchRef.current !== null && !searchRef.current.contains(e.target)) {
  //       // input 체크 해제
  //       alert("click");
  //     }
  //   }

  //   // 이벤트 리스너에 handleFocus 함수 등록
  //   document.addEventListener("mouseup", handleFocus);
  //   return () => {
  //     document.removeEventListener("mouseup", handleFocus);
  //   };
  // }, [searchRef]);

  return (
    <Wrapper
      ref={ref}
      id="addrInput"
      // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
      role="combobox"
      aria-owns="ex-list-box"
      aria-haspopup="listbox"
      aria-expanded={hasSuggestions}
    >
      <Input
        // ref={searchRef}
        maxLength={200}
        autoComplete="list"
        value={props.value || props.defaultValue || ""}
        onChange={handleInput}
        onKeyDown={handleKeyDown}
        disabled={props.isDisabled ? props.isDisabled : !ready}
        placeholder="Please enter an address"
        type="text"
        aria-autocomplete="list"
        aria-controls="ex-list-box"
        aria-activedescendant={
          currIndex !== null ? `ex-list-item-${currIndex}` : undefined
        }
        tabIndex={props.tabIndex}
        onKeyUp={(e) => {
          if (e.key === "Enter") {
            handleKeyPress();
          }
        }}
        onBlur={handleBlur}
        isValidated={props.isValidated}
        data-testid={props["data-testid"] ?? "atoms-input-address-input-input"}
      />

      {hasSuggestions && (
        <ul
          className={styles.addr_ul}
          id="ex-list-box"
          onMouseLeave={handleLeave}
          role="listbox"
        >
          {renderSuggestions()}
        </ul>
      )}
    </Wrapper>
  );
};

const AddressInput = (props: {
  tabIndex?: number;
  isLoaded: boolean;
  onChangeLocationSelect: (
    address: google.maps.LatLngLiteral,
    addressString: string,
    addressComponents?: AddressComponents | undefined
  ) => void;
  onChange?: (e: any) => void;
  defaultValue?: string;
  value?: string;
  isDisabled?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  searchQuery?: string;
  isValidated?: boolean;
  "data-testid"?: string;
}): JSX.Element => {
  // const { isLoaded, loadError } = useLoadScript({
  //   googleMapsApiKey:`${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`,
  //   language:`en`,
  //   libraries :googleMapsLibraries
  // });

  return (
    <>
      {props.isLoaded ? (
        <PlacesAutocomplete
          defaultValue={props.defaultValue}
          onChangeLocationSelect={props.onChangeLocationSelect}
          isDisabled={props.isDisabled}
          value={props.value ?? ""}
          onChange={props.onChange}
          searchQuery={props.searchQuery}
          isValidated={props.isValidated}
          tabIndex={props.tabIndex}
          data-testid={props["data-testid"]}
        />
      ) : null}
    </>
  );
};

export default AddressInput;
